import React, { useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Text } from '../Typography';
import PdfPreview from './PdfPreview';
import { useWetInkFlow } from './useWetInkFlow';
import { ErrorNote, WetInkOutcome } from './WetInkOutcome';
import { ACCEPTED_TYPES, validateScan } from './wetInkScan';
import { DocumentType, IpfsDocument } from './ipfsDocumentTypes';
import {
  Body,
  ControlsColumn,
  Details,
  DocumentLabel,
  HeaderSection,
  HeaderSubtitle,
  HeaderTitle,
  OutlinedButton,
  PreviewColumn,
  PrimaryButton,
  ProcessingButton,
  Section,
  SignatureHeading,
} from './signerStyles';

// TESTING OVERRIDE — lets a scan the wet-ink check rejected release the host flow anyway, so
// gated wizards (BecomeTrust, land verification) can be walked end-to-end without producing a
// genuinely hand-signed document. Set to false before this reaches production.
//
// Frontend-only: POST /ipfs-document/:id/wet-ink-signature still 400s, so no signature is
// recorded and the document stays un-finalised (`status !== "completed"`). All this does is
// fire `onCompleted` without one, and it stays behind an explicit click so it can never be
// mistaken for a pass.
const ALLOW_UNVERIFIED_SCAN = true;

interface WetInkFlowProps {
  /** The document type to create and sign — hardcoded per flow (e.g. a covenant/deed). */
  type: DocumentType;
  /** Optional pre-filled template variable values, keyed by placeholder token. */
  templateVars?: Record<string, string>;
  /**
   * Fired once an uploaded scan is accepted and the document is fully signed (status "completed").
   * Called with no document when the testing override above released the step instead.
   */
  onCompleted?: (document?: IpfsDocument) => void;
}

/**
 * Self-serve wet-ink flow for a logged-in verifier who is the document's sole signer. One "download"
 * lazily creates the fixed-`type` document and downloads the exact PDF to print; the verifier
 * hand-signs it and uploads the scan for automated verification. No document id, signer selection,
 * or delegation — download, sign, upload.
 */
const WetInkFlow: React.FC<WetInkFlowProps> = ({ type, templateVars, onCompleted }) => {
  const {
    documentId,
    isCompleted,
    referenceUrl,
    downloadLoading,
    downloadError,
    handleDownload,
    uploadLoading,
    outcome,
    handleUpload,
    clearOutcome,
  } = useWetInkFlow({ type, templateVars });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [scan, setScan] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [bypassed, setBypassed] = useState(false);

  const docType = type.replace(/_/g, ' ');

  // A rejected ("tampered") or unavailable ("transient") verdict can be pushed through while the
  // override is on. A guard error ("download the document first") never can — without a document
  // there is nothing to have signed.
  const canBypass =
    ALLOW_UNVERIFIED_SCAN && !bypassed && (outcome?.kind === 'tampered' || outcome?.kind === 'transient');

  const handleBypass = () => {
    setBypassed(true);
    onCompleted?.();
  };

  const handleFile = (file: File | null) => {
    setFileError(null);
    setScan(null);
    if (!file) {return;}
    const error = validateScan(file);
    if (error) {
      setFileError(error);
      return;
    }
    setScan(file);
  };

  const onUpload = async () => {
    if (!scan) {return;}
    const result = await handleUpload(scan);
    // On a clean result the scan is consumed; keep it on failure so the user can retry a transient error.
    if (result.kind === 'success') {
      setScan(null);
      if (fileInputRef.current) {fileInputRef.current.value = '';}
      // Sole-signer flow: a successful upload finalizes the document. Notify the host so gated
      // flows (e.g. an onboarding wizard) can advance.
      if (result.document.status === 'completed') {
        onCompleted?.(result.document);
      }
    }
  };

  return (
    <Details>
      <HeaderSection>
        <HeaderTitle>Wet-Ink Signing — {docType}</HeaderTitle>
        <HeaderSubtitle>
          Download the document to print, hand-sign it, scan it, and upload the scan for automated
          integrity verification.
        </HeaderSubtitle>
      </HeaderSection>

      <Body horizontal={false}>
        <PreviewColumn>
          <DocumentLabel>
            <span className="prefix">{documentId ? `IPFS Document #${documentId}` : 'New document'}</span>
            <span>{` : ${docType}`}</span>
          </DocumentLabel>
          {referenceUrl ? (
            <PdfPreview fileUrl={referenceUrl} overlayLabel="This is the exact copy to print and sign." />
          ) : (
            <Box
              sx={{
                height: 720,
                borderRadius: '20px',
                border: '1px dashed #ddd',
                backgroundColor: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: 3,
              }}
            >
              <Text small sx={{ color: '#999', maxWidth: 320 }}>
                Download the document for signing to preview it here.
              </Text>
            </Box>
          )}
        </PreviewColumn>

        <ControlsColumn>
          <Section>
            <SignatureHeading>1 · Download document to sign</SignatureHeading>
            <Text small sx={{ color: '#666', mt: -1, mb: 2, lineHeight: '18px' }}>
              This creates your document and downloads the exact copy to print. Always re-download
              before signing — re-downloading reuses the same document.
            </Text>
            {downloadLoading ? (
              <ProcessingButton disabled variant="contained">
                <CircularProgress size={20} sx={{ color: 'white' }} />
              </ProcessingButton>
            ) : (
              <OutlinedButton onClick={handleDownload} variant="outlined" startIcon={<DownloadOutlinedIcon />}>
                {documentId ? 'Re-download for signing' : 'Download for signing'}
              </OutlinedButton>
            )}
            {downloadError && <ErrorNote>{downloadError}</ErrorNote>}
          </Section>

          <Section>
            <SignatureHeading>2 · Upload the signed scan</SignatureHeading>
            <Text small sx={{ color: '#666', mt: -1, mb: 2, lineHeight: '18px' }}>
              Scan or photograph the hand-signed pages into a single PDF, JPEG, or PNG (max 10MB).
            </Text>

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES.join(',')}
              style={{ display: 'none' }}
              onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                padding: '14px 16px',
                borderRadius: '12px',
                border: '1px dashed #ccc',
                backgroundColor: '#fafafa',
                cursor: 'pointer',
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadFileOutlinedIcon sx={{ color: '#666' }} />
              <Box sx={{ minWidth: 0 }}>
                <Text small sx={{ fontWeight: 600, color: '#333', wordBreak: 'break-word' }}>
                  {scan ? scan.name : 'Choose a scanned file…'}
                </Text>
                {scan && (
                  <Text small sx={{ color: '#999', fontSize: '12px' }}>
                    {(scan.size / (1024 * 1024)).toFixed(2)} MB
                  </Text>
                )}
              </Box>
            </Box>
            {fileError && <ErrorNote>{fileError}</ErrorNote>}

            <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
              {uploadLoading ? (
                <ProcessingButton disabled variant="contained">
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                </ProcessingButton>
              ) : (
                <PrimaryButton
                  onClick={onUpload}
                  disabled={!scan || !documentId || isCompleted}
                  variant="contained"
                >
                  Upload & verify
                </PrimaryButton>
              )}
              {outcome && !uploadLoading && (
                <OutlinedButton onClick={clearOutcome} variant="outlined">
                  Clear result
                </OutlinedButton>
              )}
            </Box>

            {!documentId && (
              <Text small sx={{ color: '#999', mt: 2 }}>
                Download the document first — it’s created on download.
              </Text>
            )}
            {isCompleted && (
              <Text small sx={{ color: '#15803d', mt: 2, fontWeight: 600 }}>
                This document is already finalized.
              </Text>
            )}
          </Section>

          {outcome && <WetInkOutcome outcome={outcome} />}

          {/* Testing override — an explicit, clearly-labelled escape from a failed verdict. */}
          {canBypass && (
            <Box
              sx={{
                backgroundColor: '#FFF4E5',
                border: '1px solid #FFD8A8',
                borderRadius: '12px',
                padding: '16px 20px',
              }}
            >
              <Text small sx={{ color: '#7A4100', display: 'block', mb: 1.5, lineHeight: '18px' }}>
                Testing override: this scan did not pass verification, so no signature was recorded and the
                document stays un-finalised. You can still continue.
              </Text>
              <OutlinedButton onClick={handleBypass} variant="outlined">
                Continue without verification
              </OutlinedButton>
            </Box>
          )}

          {bypassed && (
            <Text small sx={{ color: '#7A4100', fontWeight: 600 }}>
              Continued without verification — this document was never signed.
            </Text>
          )}
        </ControlsColumn>
      </Body>
    </Details>
  );
};

export default WetInkFlow;
